import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const OFFSET_PX = 10;
const VIEWPORT_MARGIN_PX = 8;
const Z_INDEX = 2000;
const DEFAULT_CLOSE_DELAY_MS = 120;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function composeHandlers(ourHandler, theirHandler) {
  return (event) => {
    theirHandler?.(event);
    if (!event.defaultPrevented) {
      ourHandler?.(event);
    }
  };
}

function composeRefs(...refs) {
  return (node) => {
    refs.forEach((ref) => {
      if (!ref) return;
      if (typeof ref === "function") {
        ref(node);
      } else if (typeof ref === "object") {
        ref.current = node;
      }
    });
  };
}

function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia("(hover: none), (pointer: coarse)");
    const update = () => setIsTouch(Boolean(media.matches));
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, []);

  return isTouch;
}

export default function Tooltip({
  text,
  children,
  placement = "top",
  maxWidth = 260,
  offset = OFFSET_PX,
  size = "default",
  allowTouch = false,
  enableClick = false,
  closeDelay = DEFAULT_CLOSE_DELAY_MS,
}) {
  const tooltipId = useId();
  const isTouchDevice = useIsTouchDevice();
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    top: -9999,
    left: -9999,
    placement: "top",
    ready: false,
  });

  const canShowTooltip = Boolean(text) && (allowTouch || !isTouchDevice);
  const preferredPlacement = placement === "bottom" ? "bottom" : "top";

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    if (!canShowTooltip || !anchorRef.current || !tooltipRef.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    const spaceAbove = anchorRect.top - VIEWPORT_MARGIN_PX;
    const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_MARGIN_PX;
    const canPlaceAbove = spaceAbove >= tooltipRect.height + offset;
    const canPlaceBelow = spaceBelow >= tooltipRect.height + offset;

    let nextPlacement = preferredPlacement;
    if (preferredPlacement === "top" && !canPlaceAbove && canPlaceBelow) {
      nextPlacement = "bottom";
    } else if (preferredPlacement === "bottom" && !canPlaceBelow && canPlaceAbove) {
      nextPlacement = "top";
    } else if (!canPlaceAbove && !canPlaceBelow) {
      nextPlacement = spaceBelow > spaceAbove ? "bottom" : "top";
    }

    const topCandidate =
      nextPlacement === "top"
        ? anchorRect.top - tooltipRect.height - offset
        : anchorRect.bottom + offset;
    const minTop = VIEWPORT_MARGIN_PX;
    const maxTop = window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN_PX;
    const top = clamp(topCandidate, minTop, Math.max(minTop, maxTop));

    const leftCandidate =
      anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;
    const minLeft = VIEWPORT_MARGIN_PX;
    const maxLeft = window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN_PX;
    const left = clamp(leftCandidate, minLeft, Math.max(minLeft, maxLeft));

    setPosition({ top, left, placement: nextPlacement, ready: true });
  }, [canShowTooltip, offset, preferredPlacement]);

  const closeTooltipNow = useCallback(() => {
    clearCloseTimer();
    setIsOpen(false);
    setPosition((prev) => ({ ...prev, ready: false }));
  }, [clearCloseTimer]);

  const openTooltip = useCallback(() => {
    if (!canShowTooltip) return;
    clearCloseTimer();
    setIsOpen(true);
  }, [canShowTooltip, clearCloseTimer]);

  const scheduleClose = useCallback(
    (delay = closeDelay) => {
      clearCloseTimer();
      closeTimerRef.current = setTimeout(() => {
        closeTooltipNow();
      }, delay);
    },
    [clearCloseTimer, closeDelay, closeTooltipNow]
  );

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen || !canShowTooltip) return;
    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, canShowTooltip, updatePosition]);

  useEffect(() => {
    if (!isOpen || !enableClick) return;
    const handleOutside = (event) => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (anchor?.contains(event.target) || tooltip?.contains(event.target)) return;
      closeTooltipNow();
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isOpen, enableClick, closeTooltipNow]);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  const anchorProps = useMemo(
    () => ({
      onMouseEnter: openTooltip,
      onMouseLeave: () => scheduleClose(),
      onFocus: openTooltip,
      onBlur: () => scheduleClose(),
      onClick: (event) => {
        if (!enableClick || !canShowTooltip) return;
        if (isOpen) {
          closeTooltipNow();
        } else {
          openTooltip();
        }
        event.stopPropagation();
      },
      onKeyDown: (event) => {
        if (event.key === "Escape") {
          closeTooltipNow();
        }
      },
    }),
    [openTooltip, scheduleClose, enableClick, isOpen, closeTooltipNow, canShowTooltip]
  );

  let anchorNode = children;
  if (isValidElement(children)) {
    const childProps = children.props || {};
    const mergedRef = composeRefs(anchorRef, children.ref, childProps.ref);
    const describedBy =
      isOpen && canShowTooltip ? tooltipId : childProps["aria-describedby"];
    anchorNode = cloneElement(children, {
      ...childProps,
      ...anchorProps,
      ref: mergedRef,
      onMouseEnter: composeHandlers(anchorProps.onMouseEnter, childProps.onMouseEnter),
      onMouseLeave: composeHandlers(anchorProps.onMouseLeave, childProps.onMouseLeave),
      onFocus: composeHandlers(anchorProps.onFocus, childProps.onFocus),
      onBlur: composeHandlers(anchorProps.onBlur, childProps.onBlur),
      onClick: composeHandlers(anchorProps.onClick, childProps.onClick),
      onKeyDown: composeHandlers(anchorProps.onKeyDown, childProps.onKeyDown),
      "aria-describedby": describedBy,
    });
  } else {
    anchorNode = (
      <span
        {...anchorProps}
        ref={anchorRef}
        aria-describedby={isOpen && canShowTooltip ? tooltipId : undefined}
        className="inline-flex items-center"
      >
        {children}
      </span>
    );
  }

  const compactMode = size === "compact";
  const tooltipNode =
    isOpen && canShowTooltip
      ? createPortal(
          <div
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={`fixed border border-slate-200 bg-white font-medium text-slate-700 shadow-lg ${
              compactMode ? "rounded-lg" : "rounded-xl"
            }`}
            onMouseEnter={openTooltip}
            onMouseLeave={() => scheduleClose()}
            style={{
              top: position.top,
              left: position.left,
              maxWidth,
              zIndex: Z_INDEX,
              padding: compactMode ? "8px 10px" : "8px 12px",
              fontSize: compactMode ? "12px" : "13px",
              lineHeight: compactMode ? "1.25" : "1.35",
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
              height: "auto",
              overflow: "hidden",
              pointerEvents: "auto",
              opacity: position.ready ? 1 : 0,
              transform: position.ready ? "translateY(0)" : "translateY(2px)",
              transition: "opacity 120ms ease, transform 120ms ease",
            }}
          >
            {text}
          </div>,
          document.body
        )
      : null;

  return (
    <>
      {anchorNode}
      {tooltipNode}
    </>
  );
}
